import { useCallback, useEffect, useMemo, useState } from 'react';
import { getInlineString, getText } from 'i18n/intl';
import { keyBy } from 'lodash';
import type { BatchEditOperation } from 'types/batchEditObject.type';
import { type TField, type TType } from '@timeedit/types';
import { Typography } from '@timeedit/ui-components';
import { Form, Modal } from '@timeedit/ui-components/antd';
import { JobObjectSelection } from '../../hooks/useJobSubmit';
import useShownFields from '../../hooks/useShownFields';
import BulkEditField from './BulkEditField';
import {
  useBulkEditHasSelection,
  useBulkEditSubmit,
  useBulkEditWarnings,
} from './common';
import './BulkEditField.scss';

type Props = {
  setOpen: (openSection: 'fields' | false) => void;
  open: boolean;
  selectedObjectsCount?: number;
  objectType: TType | null;
  jobObjectSelection: JobObjectSelection;
  onSubmitSuccess?: () => void;
};

const BulkEditModal = ({
  setOpen,
  open,
  objectType,
  selectedObjectsCount = 0,
  jobObjectSelection,
  onSubmitSuccess,
}: Props) => {
  const shownFields = useShownFields(objectType);
  const [sendingBatch, setSendingBatch] = useState(false);

  const [form] = Form.useForm();

  useEffect(() => {
    if (open) {
      form.resetFields();
    }
  }, [open, form]);

  const buildOperations = useCallback(
    (validationResult: Record<string, any>) =>
      transformFormDataToBatchEditOperation(validationResult, shownFields),
    [shownFields]
  );

  const onSubmit = useBulkEditSubmit({
    form,
    setSendingBatch,
    setOpen,
    selection: jobObjectSelection,
    operationType: 'EDIT_FIELDS',
    buildOperations,
    successMessage: getText('object_manager.bulk-edit-modal.success-message'),
    onSuccess: onSubmitSuccess,
  });

  const fieldMap = useMemo(() => keyBy(shownFields, 'id'), [shownFields]);
  const warnings = useBulkEditWarnings(form, fieldMap, selectedObjectsCount);

  const hasSelection = useBulkEditHasSelection(form);

  if (!objectType) {
    return null;
  }

  return (
    <Modal
      open={open}
      okText={getText('object_manager.bulk-edit-modal.okText')}
      onOk={onSubmit}
      okButtonProps={{
        loading: sendingBatch,
        disabled: hasSelection === false,
      }}
      cancelText={getText('cancel')}
      onCancel={() => setOpen(false)}
      title={
        <div className="bulk-edit-modal-title-wrapper">
          {getText('object_manager.bulk-edit-modal.title')}
          <Typography.Text>
            {getInlineString(
              'object_manager.bulk-edit-modal.subtitle',
              selectedObjectsCount,
              objectType.name
            )}
          </Typography.Text>
        </div>
      }
    >
      <Form form={form} layout="horizontal" requiredMark={false}>
        <div className="bulk-edit-wrapper">
          {shownFields.map((field) => (
            <BulkEditField form={form} key={field?.id} field={field} />
          ))}
        </div>
        {warnings}
      </Form>
    </Modal>
  );
};

export const transformFormDataToBatchEditOperation = (
  formData: Record<string, any>,
  fields: TField[]
): BatchEditOperation[] => {
  const operations: BatchEditOperation[] = [];

  fields.forEach((field) => {
    const fieldId = field.id;

    if (formData[`${fieldId}`] == null || formData[`${fieldId}`] === 'keep') {
      return;
    }

    if (formData[fieldId] === 'add') {
      if (formData[`${fieldId}_add`] == null) {
        throw new Error('Add value is required');
      }
      operations.push({
        category: 'field',
        fieldId: fieldId,
        operation: 'add',
        value: formData[`${fieldId}_add`],
      });
      return;
    }

    if (formData[`${fieldId}`] === 'findReplace') {
      if (
        formData[`${fieldId}_with`] == null ||
        formData[`${fieldId}_replace`] == null
      ) {
        throw new Error('Both search and replace values are required');
      }
      operations.push({
        category: 'field',
        fieldId: fieldId,
        operation: 'findReplace',
        searchValue: formData[`${fieldId}_replace`],
        value: formData[`${fieldId}_with`],
        rule:
          formData[`${fieldId}_rule`] ??
          (field.multiple ? 'matchAll' : 'matchOne'),
      });
      return;
    }

    if (formData[`${fieldId}`] === 'replaceAll') {
      if (formData[`${fieldId}_replaceAll`] == null) {
        throw new Error('Replace all value is required');
      }
      operations.push({
        category: 'field',
        fieldId: fieldId,
        operation: 'replaceAll',
        value: formData[`${fieldId}_replaceAll`],
      });
      return;
    }

    if (formData[`${fieldId}`] === 'removeSpecific') {
      if (formData[`${fieldId}_removeSpecific`] == null) {
        throw new Error('Remove specific value is required');
      }
      operations.push({
        category: 'field',
        fieldId: fieldId,
        operation: 'removeSpecific',
        searchValue: formData[`${fieldId}_removeSpecific`],
      });
      return;
    }

    if (formData[fieldId] === 'removeAll') {
      operations.push({
        category: 'field',
        fieldId: fieldId,
        operation: 'removeAll',
      });
      return;
    }

    if (formData[fieldId] === 'checkAll') {
      operations.push({
        category: 'field',
        fieldId: fieldId,
        operation: 'checkAll',
      });
      return;
    }

    if (formData[fieldId] === 'uncheckAll') {
      operations.push({
        category: 'field',
        fieldId: fieldId,
        operation: 'uncheckAll',
      });
      return;
    }
  });

  return operations;
};

export default BulkEditModal;
